import type { AxiosResponse } from 'axios';
import { api } from '@/services/api/main/axios';
import _ from 'lodash';
import type { User } from '@/services/models';
import { loadLanguageAsync } from '@/i18n';

export const getUserApiRequest = async (): Promise<User> => {
  const res = await api.get<{ payload: User }>('user');
  const payload = res.data.payload;
  if (payload.workspaces) {
    payload.workspaces = _.orderBy(payload.workspaces, ['deletable', 'name'], ['asc', 'asc']);
  }
  await loadLanguageAsync(payload.language || 'en');
  return payload;
};

export type UpdateProfileForm = Pick<
  User,
  'currencyId' | 'phoneNumber' | 'language' | 'messengerType' | 'messengerAccount'
>;

export const putUserApiRequest = async (payload: Partial<User>): Promise<User> => {
  const res = await api.put<{ payload: User }>('user', payload);
  return res.data.payload;
};

export const patchUserApiRequest = async (
  payload: Partial<User>,
): Promise<AxiosResponse<{ payload: User }>> => {
  return api.patch<{ payload: User }>('user', payload);
};

/** Security settings (API keys, sessions, landing key, 2FA) — same axios layer as above. */

export const getUserApiKeysApiRequest = async (config?: object) => {
  const res = await api.get<{ payload: unknown }>('user/api-keys', config);
  return res.data.payload;
};

export const postCreateUserApiKeyApiRequest = async (params: object) => {
  const res = await api.post<{ payload: unknown }>('user/api-keys', params);
  return res.data.payload;
};

export const deleteUserApiKeyApiRequest = async (id: string) => {
  const res = await api.delete<{ payload: unknown }>(`user/api-keys/${id}`);
  return res.data.payload;
};

export const patchUserApiKeyApiRequest = async (id: string, params: object) => {
  const res = await api.patch<{ payload: unknown }>(`user/api-keys/${id}`, params);
  return res.data.payload;
};

export const getUserAccessTokensApiRequest = async (config?: object) => {
  const res = await api.get<{ payload: unknown }>('user/access-tokens', config);
  return res.data.payload;
};

export const deleteUserAccessTokenApiRequest = async (id: string) => {
  const res = await api.delete<{ payload: unknown }>(`user/access-tokens/${id}`);
  return res.data.payload;
};

export const deleteAllUserAccessTokensApiRequest = async () => {
  const res = await api.delete<{ payload: unknown }>('user/access-tokens');
  return res.data.payload;
};

export const getUserLandingSecretKeyApiRequest = async () => {
  const res = await api.get<{ payload: { key: string } }>('user/landing-key');
  return res.data.payload;
};

export const postUserTwoFactorGetQrCodeApiRequest = async (payload: object) => {
  const res = await api.post<{ payload: unknown }>('user/two-factor/get-qr-code', payload);
  return res.data.payload;
};

export const postUserTwoFactorVerifyQrCodeApiRequest = async (payload: object) => {
  const res = await api.post<{ payload: unknown }>('user/two-factor/verify-qr-code', payload);
  return res.data.payload;
};

export const postUserTwoFactorDisableApiRequest = async (payload: object) => {
  const res = await api.post<{ payload: unknown }>('user/two-factor/disable', payload);
  return res.data.payload;
};

export const postUserUploadConversionsApiRequest = async (payload: { conversions: string }) => {
  const res = await api.post<{ payload: unknown }>('user/upload-conversions', payload);
  return res.data.payload;
};

export const postSendPostbacksApiRequest = async (
  payload: object,
): Promise<Record<string, unknown>> => {
  const res = await api.post<{ payload: Record<string, unknown> }>('user/send-postbacks', payload);
  return res.data.payload;
};

export const getUserTemporaryPasswordApiRequest = async (): Promise<{
  password?: string;
  expireAt?: string;
}> => {
  const res = await api.get<{ payload: { password?: string; expireAt?: string } }>(
    'user/support-password',
  );
  return res.data.payload;
};

export const getMemberAclApiRequest = async (payload: {
  userId: string;
  workspaceIds?: unknown;
}): Promise<unknown[]> => {
  const res = await api.get<{ payload: unknown[] }>(`user/members/${payload.userId}/permissions`, {
    params: { workspaceIds: payload.workspaceIds },
  });
  return res.data.payload;
};

export const getMemberApiRequest = async (memberId: string): Promise<unknown> => {
  const res = await api.get<{ payload: unknown }>(`user/members/${memberId}`);
  return res.data.payload;
};

export const getMembersApiRequest = async (): Promise<unknown[]> => {
  const res = await api.get<{ payload: unknown[] }>('user/members');
  return res.data.payload;
};

export const postMemberApiRequest = async (payload: object) => {
  const res = await api.post<{ payload: unknown }>('user/members', payload);
  return res.data.payload;
};

export const putMemberApiRequest = async (memberId: string, payload: object) => {
  const res = await api.put<{ payload: unknown }>(`user/members/${memberId}`, payload);
  return res.data.payload;
};

export const deleteMemberApiRequest = async (memberId: string) => {
  const res = await api.delete<{ payload: unknown }>(`user/members/${memberId}`);
  return res.data.payload;
};

export const getMemberItemsRelationsApiRequest = async (params: { workspaceId: string }) => {
  const res = await api.get<{ payload: object }>('user/members/items-relations', {
    params: { workspaceId: params.workspaceId },
  });
  return res.data.payload;
};

export const putMemberAclApiRequest = async (userId: string, body: object) => {
  await api.put(`user/members/${userId}/permissions`, body);
};

export const postMemberResetPasswordApiRequest = async (memberId: string) => {
  const res = await api.post<{ payload: unknown }>(`/user/members/${memberId}/reset-password`);
  return res.data.payload;
};
